/**
 * Shared parsing for the persisted counters that cap how many times the app may reload
 * itself. There are two of them (auth-recovery attempts, AUTH_TOKEN reply navigations)
 * and they had independent copies of this clamp — one of which lived in a file the vitest
 * runner cannot reach, so it was untested and free to drift.
 */

/**
 * Parse a persisted bound counter, clamped into [0, max].
 *
 * The two directions are deliberately NOT symmetric, which is why this clamps rather than
 * merely validating:
 *  - Unusable input (NaN, '1.5', negatives) → 0. `Number('abc')` is NaN, `NaN >= max` is
 *    false and `NaN + 1` writes back "NaN", so garbage would silently REMOVE the bound.
 *    Resetting to 0 is safe: the counter then writes clean integers and reaches the bound
 *    normally.
 *  - Absurdly large values → clamped UP to max, not down to 0. `Number.isInteger` does not
 *    reject '1e99' (1e99 IS an integer in JS), and mapping a huge value to 0 would be the
 *    WRONG correction — it re-enables the reloads the counter exists to stop. At-or-above
 *    max means "exhausted".
 */
export const parseBoundedCounter = (raw: string | null, max: number): number => {
  const n = Number(raw);
  if (!Number.isInteger(n) || n <= 0) return 0;
  return Math.min(n, max);
};

/**
 * A counter persisted in sessionStorage, with an in-memory floor.
 *
 * The floor is load-bearing, not belt-and-braces. sessionStorage throws in an Android
 * WebView with DOM storage disabled — the case every try/catch on this path exists for —
 * and a storage-only counter silently reads 0 forever there, which removes the bound
 * entirely on exactly the devices most likely to need it. Taking `max(memory, storage)`
 * keeps the within-document bound working when storage is unavailable, while storage
 * provides the across-document bound that a module variable cannot (a document load
 * resets module state).
 */
export const createBoundedSessionCounter = (key: string, max: number) => {
  let inMemory = 0;
  let warnedUnavailable = false;

  // The degradation this counter is built to survive is otherwise invisible, and it changes
  // what a bug report means: on such a device a bound that must survive a document load does
  // not hold at all.
  //
  // Raw console.error here, and ONLY here, for a mechanical reason rather than a stylistic
  // one: Logger reads `import.meta.env`, and this module has to stay importable by the
  // node-env vitest runner that gates it. (Not "because Logger is ERROR-only in production" —
  // that would be an argument FOR Logger.error, which is exactly the level that still emits
  // there. Every other new site in this change uses Logger.) Once per counter, because the
  // read path runs on every render.
  const warnUnavailable = (): void => {
    if (warnedUnavailable) return;
    warnedUnavailable = true;
    console.error(`DOM storage unavailable for "${key}"; loop bound degraded to in-memory only`);
  };

  const read = (): number => {
    let stored = 0;
    try {
      stored = parseBoundedCounter(sessionStorage.getItem(key), max);
    } catch {
      warnUnavailable();
    }
    return Math.max(inMemory, stored);
  };

  const bump = (): number => {
    const next = Math.min(read() + 1, max);
    inMemory = next;
    try {
      sessionStorage.setItem(key, String(next));
    } catch {
      // Degrades to the in-memory bound; never throw on the auth boot path.
      warnUnavailable();
    }
    return next;
  };

  const clear = (): void => {
    inMemory = 0;
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* noop */
    }
  };

  return { read, bump, clear };
};
